import { useState, useMemo } from "react";
import { Check, X, Ban, Flag, SlidersHorizontal } from "lucide-react";
import { useAdminData } from "../../hooks/useAdminData";
import { reports as reportsApi, postings as postingsApi } from "../../api/endpoints";
import { AddToast } from "../../components/feedback/Toast";
import { Btn } from "../../components/ui/Btn";
import { Card } from "../../components/ui/Card";
import { FilterDrawer, FilterFieldDef } from "../../components/ui/FilterDrawer";
import { PageHeader } from "../../components/ui/PageHeader";
import { SearchBar } from "../../components/ui/SearchBar";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { CORAL, F, T } from "../../theme/tokens";
import { TableWrap } from "../../components/ui/TableWrap";

// ── Postings ──────────────────────────────────────────────────────────────────

export function PostingsView({ addToast }: { addToast: AddToast }) {
  const { ADMIN_POSTINGS, refetchPostings, refetchReports } = useAdminData();

  function setPostingStatus(id: string, title: string, status: string, message: string) {
    postingsApi.update(id, { status })
      .then(() => { refetchPostings(); addToast(message, status === "Closed" ? "info" : "success"); })
      .catch((e) => addToast(e instanceof Error ? e.message : "Failed to update posting", "error"));
  }

  function flagPosting(p: { id: string; title: string; orgName: string }) {
    const description = window.prompt(`Why are you flagging "${p.title}"?`, "Content review");
    if (description === null) return;
    reportsApi.create({
      reported_entity_type: "Posting",
      reported_entity_id: p.id,
      reported_entity_name: p.title,
      reason: "Inappropriate content",
      description: description || undefined,
      severity: "Medium",
    })
      .then(() => { refetchReports(); addToast("Flagged for moderation", "info"); })
      .catch((e) => addToast(e instanceof Error ? e.message : "Failed to flag posting", "error"));
  }
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [orgFilter, setOrgFilter] = useState("All");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const uniqueOrgs = useMemo(() => ["All", ...Array.from(new Set(ADMIN_POSTINGS.map((p) => p.orgName))).sort()], [ADMIN_POSTINGS]);

  const postingFilterFields: FilterFieldDef[] = [
    { type: "chips", key: "status", label: "Status", options: ["All", "Active", "Draft", "Closed", "Pending Review"] },
    { type: "date", key: "dateFrom", label: "Posted from" },
    { type: "date", key: "dateTo", label: "Posted to" },
    { type: "select", key: "org", label: "Organisation", options: uniqueOrgs },
  ];
  const filterValues = { status: statusFilter, dateFrom, dateTo, org: orgFilter };

  const filtered = useMemo(() => ADMIN_POSTINGS.filter((p) => {
    const s = search.toLowerCase();
    const match = !s || p.title.toLowerCase().includes(s) || p.orgName.toLowerCase().includes(s) || p.category.toLowerCase().includes(s);
    const ms = statusFilter === "All" || p.status === statusFilter;
    const mo = orgFilter === "All" || p.orgName === orgFilter;
    return match && ms && mo;
  }), [ADMIN_POSTINGS, search, statusFilter, orgFilter]);

  const hasFilter = statusFilter !== "All" || dateFrom !== "" || dateTo !== "" || orgFilter !== "All";

  return (
    <div>
      <PageHeader title="Postings" sub={`${ADMIN_POSTINGS.length} postings`} />

      <FilterDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} title="Postings" fields={postingFilterFields} values={filterValues}
        onApply={(v) => { setStatusFilter(v.status ?? "All"); setDateFrom(v.dateFrom ?? ""); setDateTo(v.dateTo ?? ""); setOrgFilter(v.org ?? "All"); }} />

      <div style={{ display: "flex", gap: "10px", marginBottom: "18px", flexWrap: "wrap" }}>
        <SearchBar value={search} onChange={setSearch} placeholder="Search postings…" />
        <button className="filter-btn" onClick={() => setDrawerOpen(true)} style={{ display: "flex", alignItems: "center", gap: "6px", padding: "7px 13px", borderRadius: "8px", fontSize: "12px", fontFamily: F, fontWeight: 500, cursor: "pointer", border: `1px solid ${hasFilter ? CORAL : T.border}`, background: hasFilter ? `rgba(209,0,75,0.06)` : T.s2, color: hasFilter ? CORAL : T.sub, transition: "all 0.15s" }}>
          <SlidersHorizontal size={13} /> Filters
        </button>
        {hasFilter && (
          <button onClick={() => { setStatusFilter("All"); setDateFrom(""); setDateTo(""); setOrgFilter("All"); }} style={{ fontSize: "11px", color: CORAL, fontFamily: F, background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Clear</button>
        )}
      </div>
      <Card>
        <TableWrap subline="Organisation"><table style={{ width: "100%", borderCollapse: "collapse", fontFamily: F }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${T.border}` }}>
              {["Title", "Organisation", "Category", "Budget", "Status", "Posted", "Applications", "Actions"].map((h) => (
                <th key={h} style={{ padding: "10px 14px", textAlign: "left", fontSize: "10px", fontWeight: 700, color: T.muted, letterSpacing: "0.06em", textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} style={{ borderBottom: `1px solid ${T.border}`, transition: "background 0.1s" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = T.s2; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = ""; }}>
                <td style={{ padding: "12px 14px" }}>
                  <p style={{ fontSize: "12px", fontWeight: 600, color: T.text, fontFamily: F }}>{p.title}</p>
                </td>
                <td style={{ padding: "12px 14px", fontSize: "12px", color: T.sub }}>{p.orgName}</td>
                <td style={{ padding: "12px 14px", fontSize: "12px", color: T.sub }}>{p.category}</td>
                <td style={{ padding: "12px 14px", fontSize: "12px", color: T.text, fontWeight: 500 }}>{p.budget}</td>
                <td style={{ padding: "12px 14px" }}><StatusBadge status={p.status} /></td>
                <td style={{ padding: "12px 14px", fontSize: "11px", color: T.muted }}>{p.posted}</td>
                <td style={{ padding: "12px 14px", fontSize: "12px", color: T.text }}>{p.totalApplications}</td>
                <td style={{ padding: "12px 14px" }}>
                  <div style={{ display: "flex", gap: "4px" }}>
                    {p.status === "Pending Review" && (
                      <>
                        <Btn variant="primary" size="xs" onClick={() => setPostingStatus(p.id, p.title, "Active", `"${p.title}" approved`)}><Check size={10} /></Btn>
                        <Btn variant="destructive" size="xs" onClick={() => setPostingStatus(p.id, p.title, "Closed", `"${p.title}" rejected`)}><X size={10} /></Btn>
                      </>
                    )}
                    {p.status === "Active" && (
                      <Btn variant="ghost" size="xs" onClick={() => setPostingStatus(p.id, p.title, "Closed", `"${p.title}" closed`)}><Ban size={10} /> Close</Btn>
                    )}
                    <Btn variant="ghost" size="xs" onClick={() => flagPosting(p)}><Flag size={10} /></Btn>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table></TableWrap>
      </Card>
    </div>
  );
}
